/**
 * Cliente del chat de ayuda. **Tres llamadas, las tres GET.**
 *
 * El chat SOLO EXPLICA: no carga, no edita, no borra, no emite y no factura.
 * Eso vale igual del lado del cliente, y aca esta hecho cumplir de una forma que
 * no depende de que nadie se acuerde: la unica puerta a la red de este archivo es
 * `api.fetchReadOnly`, que no acepta metodo ni cuerpo. Para escribir algo desde
 * el chat habria que importar otra cosa, y eso se ve en la revision.
 *
 * Del otro lado, las tres vistas del backend definen `get()` y nada mas, asi que
 * cualquier otro verbo contesta 405 (ver `nucleo/modules/base/api_guia.py`).
 *
 * El chat se apaga solo cuando no hay guia
 * ----------------------------------------
 * `esInicioValido` no es paranoia de tipos. La version de demostracion que se le
 * entrega al cliente —`muestra/Nucleo-ERP-sistema.html`— corre sin servidor, con
 * un backend falso que contesta `{detail, results: [], count: 0}` con estado 200
 * a toda ruta que no tenga grabada. O sea que la respuesta llega "bien" y viene
 * vacia. Sin esta comprobacion, el boton de ayuda aparecia ahi y contestaba la
 * nada; con ella, si la guia no esta, el boton directamente no se dibuja. Un
 * chat que no puede contestar es mejor cerrado que abierto y mudo.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { useSessionStore } from '@/store/session';

// ---------------------------------------------------------------------------
// Lo que devuelve el servidor
// ---------------------------------------------------------------------------
/** Adonde lleva el boton «Ir a la pantalla». `null` cuando no hay adonde ir. */
export interface EnlaceGuia {
  /** Ruta del frontend: `/m/<modelo>?menu=<nombre>`, `/panel`, `/buscar` o `/`. */
  ruta: string;
  /** Como se llama esa pantalla, para que el boton diga adonde va. */
  pantalla: string;
  menu: string;
}

/** Un proceso resumido: lo que necesita un boton de «quizas buscabas…». */
export interface ProcesoResumen {
  id: string;
  titulo: string;
  area: string;
  pregunta_principal: string;
  donde_se_hace: string;
  enlace: EnlaceGuia | null;
  verificado: boolean;
  puntaje?: number;
}

/** Donde se mira un dato concreto, con su pantalla. */
export interface DondeSeVe {
  pregunta: string;
  pantalla: string;
  aclaracion: string;
  enlace: EnlaceGuia | null;
}

/** Un proceso completo: es la respuesta que se dibuja. */
export interface ProcesoCompleto extends ProcesoResumen {
  pasos: string[];
  trampas: string[];
  no_hace: string[];
  /**
   * Obligatorio cuando `verificado` es `false`, y se muestra SIEMPRE.
   *
   * Es lo que separa «esto lo probamos contra el sistema» de «esto creemos que
   * es asi». Dibujar los pasos sin el aviso seria la version sutil de inventar.
   */
  aviso: string | null;
  donde_se_ve: DondeSeVe[];
}

export type EstadoBusqueda = 'encontrado' | 'no_encontrado' | 'saludo';

export interface RespuestaBusqueda {
  consulta: string;
  estado: EstadoBusqueda;
  /** `null` salvo cuando `estado === 'encontrado'`. Nunca se rellena por las dudas. */
  respuesta: ProcesoCompleto | null;
  alternativas: ProcesoResumen[];
  mensaje: string | null;
  /** `{lo que escribio: lo que entendimos}` para las palabras corregidas por tipeo. */
  entendi: Record<string, string>;
  aclaracion: string;
}

export interface AreaGuia {
  id: string;
  titulo: string;
  descripcion: string;
}

export interface PreguntaSugerida {
  id: string;
  pregunta: string;
  area: string;
}

export interface InicioGuia {
  version: string;
  actualizado: string;
  procesos: number;
  areas: AreaGuia[];
  sugeridas: PreguntaSugerida[];
  /** El renglon fijo. Viene del servidor para que diga lo mismo en todos lados. */
  aclaracion: string;
  frontera: string;
}

export interface RespuestaProceso {
  aclaracion: string;
  respuesta: ProcesoCompleto;
  alternativas: ProcesoResumen[];
}

// ---------------------------------------------------------------------------
// Las tres llamadas
// ---------------------------------------------------------------------------
const RAIZ = '/guia';

/** Con que abrir el chat: preguntas sugeridas, areas y la aclaracion fija. */
export function fetchInicioGuia(signal?: AbortSignal): Promise<InicioGuia> {
  return api.fetchReadOnly<InicioGuia>(`${RAIZ}/`, undefined, signal);
}

/** La pregunta del vendedor. Puede contestar «no encontre», y esta bien que lo haga. */
export function buscarEnLaGuia(consulta: string, signal?: AbortSignal): Promise<RespuestaBusqueda> {
  return api.fetchReadOnly<RespuestaBusqueda>(`${RAIZ}/buscar/`, { q: consulta }, signal);
}

/** Un proceso puntual, para cuando se toca un «quizas buscabas…». */
export function leerProcesoDeLaGuia(id: string, signal?: AbortSignal): Promise<RespuestaProceso> {
  return api.fetchReadOnly<RespuestaProceso>(
    `${RAIZ}/proceso/${encodeURIComponent(id)}/`,
    undefined,
    signal,
  );
}

/**
 * ¿Esta respuesta es una guia de verdad?
 *
 * Ver el docstring del modulo: el backend falso de la version de demostracion
 * contesta 200 con un objeto vacio a cualquier ruta que no conozca, asi que
 * «llego sin error» no alcanza para decidir que el chat puede abrir.
 */
export function esInicioValido(datos: unknown): datos is InicioGuia {
  if (datos === null || typeof datos !== 'object') return false;
  const posible = datos as Partial<InicioGuia>;
  return (
    Array.isArray(posible.sugeridas) &&
    posible.sugeridas.length > 0 &&
    typeof posible.aclaracion === 'string' &&
    posible.aclaracion !== ''
  );
}

// ---------------------------------------------------------------------------
// El hook
// ---------------------------------------------------------------------------
export const CLAVE_INICIO_GUIA = ['guia', 'inicio'] as const;

/**
 * La guia de arranque. No depende de la empresa activa —el contenido es el mismo
 * para todo el sistema— asi que la clave no la incluye y no se recarga al
 * cambiar de consorcio.
 *
 * `retry: false` porque el unico motivo realista para que esto falle es que el
 * despliegue no tenga el endpoint. Reintentarlo tres veces solo demora el
 * momento en que el boton decide no dibujarse.
 */
export function useInicioGuia(): UseQueryResult<InicioGuia> {
  const autenticado = useSessionStore((estado) => estado.status === 'authenticated');

  return useQuery({
    queryKey: CLAVE_INICIO_GUIA,
    queryFn: ({ signal }) => fetchInicioGuia(signal),
    enabled: autenticado,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}
